/** Display helpers for random pipe compatibility — PRD section 5 rules. */

import type { CompatPerPipeRow, CompatReportDocument } from '../types';

const CONFIDENCE_Z: Record<number, number> = {
  80: 1.2816,
  90: 1.6449,
  95: 1.96,
  99: 2.5758,
};

/** Format API rate (0..1) as percentage string. */
export function formatPct(rate: number, digits = 1): string {
  return `${(rate * 100).toFixed(digits)}%`;
}

/**
 * Give-or-take in pipes from margin fraction × pipesTotal (PRD rule 2).
 * Uses progress.giveOrTake when provided; otherwise weldability.marginOfError × pipesTotal.
 */
export function giveOrTakePipes(
  marginOfError: number,
  pipesTotal: number,
  progressGiveOrTake?: number | null
): number {
  if (progressGiveOrTake != null && Number.isFinite(progressGiveOrTake)) {
    return Math.round(progressGiveOrTake);
  }
  return Math.round(marginOfError * pipesTotal);
}

/** Planning estimate for first-read runs (sample UI formula; not sent to API). */
export function estimateFirstReadRuns(confidencePct: number, marginPct: number): number {
  const z = CONFIDENCE_Z[confidencePct] ?? CONFIDENCE_Z[95];
  const m = marginPct / 100;
  if (m <= 0) return 0;
  return Math.ceil(Math.pow(z / m, 2) * 0.25);
}

export function bandLabel(band: string): string {
  if (band === 'atRisk') return 'At risk';
  if (!band) return '';
  return band.charAt(0).toUpperCase() + band.slice(1);
}

export function formatSeamSeparationMm(mm: number | null | undefined, degFallback?: number): string {
  if (mm != null && Number.isFinite(mm)) {
    return `${Math.round(mm)} mm`;
  }
  if (degFallback != null && Number.isFinite(degFallback)) {
    return `${degFallback} deg`;
  }
  return '—';
}

/**
 * PRD rule 12: `project.fitDataUpdatedAt` for the header vintage line.
 * Display as "24 July 2026" (and "24 July 2026 16:53" when time is present).
 */
export function formatFitDataUpdatedAt(value: string | null | undefined): string | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ] as const;

  const m = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::\d{2})?)?/
  );
  if (m) {
    const year = Number(m[1]);
    const monthIdx = Number(m[2]) - 1;
    const day = Number(m[3]);
    if (monthIdx < 0 || monthIdx > 11 || !Number.isFinite(day) || !Number.isFinite(year)) {
      return raw;
    }
    const datePart = `${day} ${months[monthIdx]} ${year}`;
    if (m[4] != null && m[5] != null) {
      return `${datePart} ${m[4]}:${m[5]}`;
    }
    return datePart;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const day = parsed.getDate();
    const month = months[parsed.getMonth()];
    const year = parsed.getFullYear();
    const hh = String(parsed.getHours()).padStart(2, '0');
    const mm = String(parsed.getMinutes()).padStart(2, '0');
    const hasTime = /[T ]\d{1,2}:\d{2}/.test(raw);
    return hasTime ? `${day} ${month} ${year} ${hh}:${mm}` : `${day} ${month} ${year}`;
  }

  return raw;
}

export type RangeStat = { min: number; typical: number; max: number };

export function formatTypical(stat: RangeStat | number | null | undefined): string {
  if (stat == null) return '—';
  if (typeof stat === 'number') return String(Math.round(stat));
  return String(Math.round(stat.typical));
}

export function formatRange(stat: RangeStat | null | undefined): string {
  if (!stat || typeof stat !== 'object') return '';
  return `${stat.min} to ${stat.max}`;
}

export type WeldabilityStatsSource = {
  pipesPlacedAvg: number;
  pipesTotal: number;
  marginOfError: number;
  distribution?: Array<{ pipes: number; orders: number }>;
};

/**
 * Confidence band in words (PRD rule 11): "at 247" or "between 245 and 249".
 */
export function bandWords(src: WeldabilityStatsSource): string {
  const spread = src.marginOfError * src.pipesTotal;
  const lo = Math.round(src.pipesPlacedAvg - spread);
  const hi = Math.round(src.pipesPlacedAvg + spread);
  return lo === hi ? `at ${lo}` : `between ${lo} and ${hi}`;
}

/**
 * Count that at least 9 builds in 10 reach — from the distribution CDF (sample-ui).
 */
export function nineInTen(src: WeldabilityStatsSource): number | null {
  const dist = src.distribution || [];
  const tot = dist.reduce((a, e) => a + e.orders, 0);
  if (!tot) return null;
  let c = 0;
  for (const e of dist) {
    c += e.orders;
    if (c > tot * 0.1) return e.pipes;
  }
  return dist[dist.length - 1]?.pipes ?? null;
}

export type PerPipeSortKey = 'pipeId' | 'band' | 'goesInRate';

export interface PerPipeDisplayRow extends CompatPerPipeRow {
  isDeadEnd: boolean;
  deadEnd?: string;
}

/**
 * Per-pipe table source (PRD rule 11).
 * When deadEnds is present, rank on withoutThem.perPipe and append set-aside pipes last.
 */
export function resolvePerPipeDisplayRows(
  weldability: CompatReportDocument['weldability']
): PerPipeDisplayRow[] {
  const asScanned = weldability?.perPipe?.pipes ?? [];
  const deadEnds = weldability?.deadEnds;
  const usable = deadEnds?.withoutThem?.perPipe;

  if (!deadEnds || !usable) {
    return asScanned.map((p) => ({ ...p, isDeadEnd: false }));
  }

  const asScannedById = new Map(asScanned.map((p) => [p.pipeId, p]));
  const deadIds = new Set(deadEnds.pipes.map((p) => p.pipeId));

  const pool: PerPipeDisplayRow[] = usable
    .filter((p) => !deadIds.has(p.pipeId))
    .map((p) => ({ ...p, isDeadEnd: false }));

  const setAside: PerPipeDisplayRow[] = deadEnds.pipes.map((d) => {
    const scanned = asScannedById.get(d.pipeId);
    return {
      pipeId: d.pipeId,
      goesInRate: scanned?.goesInRate ?? 0,
      band: scanned?.band ?? 'rework',
      partnersEndA: scanned?.partnersEndA ?? d.partnersA,
      partnersEndB: scanned?.partnersEndB ?? d.partnersB,
      isDeadEnd: true,
      deadEnd: d.deadEnd,
    };
  });

  return [...pool, ...setAside];
}

/** Sort usable-pool rows; dead-end pipes stay last (never rank 1). */
export function sortPerPipeDisplayRows(
  rows: PerPipeDisplayRow[],
  sortKey: PerPipeSortKey,
  dir: 1 | -1
): PerPipeDisplayRow[] {
  const pool = rows.filter((r) => !r.isDeadEnd);
  const setAside = rows.filter((r) => r.isDeadEnd);

  pool.sort((a, b) => {
    if (sortKey === 'pipeId') return dir * a.pipeId.localeCompare(b.pipeId);
    if (sortKey === 'band') return dir * String(a.band).localeCompare(String(b.band));
    return dir * (a.goesInRate - b.goesInRate);
  });

  setAside.sort((a, b) => {
    if (sortKey === 'pipeId') return dir * a.pipeId.localeCompare(b.pipeId);
    if (sortKey === 'band') return dir * String(a.band).localeCompare(String(b.band));
    return dir * (a.goesInRate - b.goesInRate);
  });

  return [...pool, ...setAside];
}

/** Build POST body: omit pipeIds when running all pipes. */
export function buildSubmitPayload(input: {
  projectNumber: string;
  /** Selected pipe size group id — omit when null/empty. */
  groupId?: string | null;
  pipeIds?: string[] | null;
  confidence: number;
  margin: number;
  hiLoThreshold?: number | null;
  weldSeamSeparationMm?: number | null;
  sixOClockExclusionDeg?: number | null;
  flippingAllowed?: boolean | null;
  /** Set-aside capacity. `0` = off; omit when null/empty (like seam overrides). */
  setAsideCapacity?: number | null;
  /** Exact build count (2..5000). Mutually exclusive with minOrders. */
  orders?: number | null;
  /** Assurance floor (1..5000). Mutually exclusive with orders. */
  minOrders?: number | null;
  /** Best N picks — mutually exclusive with pipesToDeliver. */
  pipesNeeded?: number | null;
  /** Must deliver N — mutually exclusive with pipesNeeded. */
  pipesToDeliver?: number | null;
}): Record<string, unknown> {
  const body: Record<string, unknown> = {
    projectNumber: input.projectNumber,
    confidence: input.confidence,
    margin: input.margin,
  };
  if (input.groupId != null && String(input.groupId).trim() !== '') {
    body.groupId = String(input.groupId).trim();
  }
  if (input.pipeIds && input.pipeIds.length > 0) {
    body.pipeIds = input.pipeIds;
  }
  if (input.hiLoThreshold != null && Number.isFinite(input.hiLoThreshold)) {
    body.hiLoThreshold = input.hiLoThreshold;
  }
  if (input.weldSeamSeparationMm != null && Number.isFinite(input.weldSeamSeparationMm)) {
    body.weldSeamSeparationMm = input.weldSeamSeparationMm;
  }
  if (input.sixOClockExclusionDeg != null && Number.isFinite(input.sixOClockExclusionDeg)) {
    body.sixOClockExclusionDeg = input.sixOClockExclusionDeg;
  }
  if (input.flippingAllowed != null) {
    body.flippingAllowed = input.flippingAllowed;
  }
  if (input.setAsideCapacity != null && Number.isFinite(input.setAsideCapacity)) {
    body.setAsideCapacity = Math.max(0, Math.round(input.setAsideCapacity));
  }
  // One choice, one field — API rejects orders + minOrders together.
  if (input.orders != null && Number.isFinite(input.orders)) {
    body.orders = Math.min(5000, Math.max(2, Math.round(input.orders)));
  } else if (input.minOrders != null && Number.isFinite(input.minOrders)) {
    body.minOrders = Math.min(5000, Math.max(1, Math.round(input.minOrders)));
  }
  // One choice, one field — API rejects pipesNeeded + pipesToDeliver together.
  if (input.pipesNeeded != null && Number.isFinite(input.pipesNeeded)) {
    body.pipesNeeded = Math.max(2, Math.round(input.pipesNeeded));
  } else if (input.pipesToDeliver != null && Number.isFinite(input.pipesToDeliver)) {
    body.pipesToDeliver = Math.max(2, Math.round(input.pipesToDeliver));
  }
  return body;
}
